import { useState } from 'react';
import { Box, Typography, Chip, IconButton, Avatar } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import CreditCardRoundedIcon    from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon      from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon   from '@mui/icons-material/PointOfSaleRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import CheckRoundedIcon         from '@mui/icons-material/CheckRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import EditRoundedIcon          from '@mui/icons-material/EditRounded';
import BlockRoundedIcon         from '@mui/icons-material/BlockRounded';

const MONEY_META = {
    transfer: { label: 'Перевод',  letter: 'П', color: '#818cf8', bg: 'rgba(129,140,248,0.10)', icon: <CreditCardRoundedIcon  sx={{ fontSize: 13 }} /> },
    cash:     { label: 'Наличные', letter: 'Н', color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: <PaymentsRoundedIcon    sx={{ fontSize: 13 }} /> },
    terminal: { label: 'Терминал', letter: 'Т', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <PointOfSaleRoundedIcon sx={{ fontSize: 13 }} /> },
};
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtMoney    = (v)   => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

/**
 * Карточка одного логического чека (общий receipt_uid) — краткий вид + раскрытие по клику.
 * props:
 *  - group:         Order[] одного чека (обязателен)
 *  - onConfirm:     (order)=>void — если передан, у неподтверждённого чека будет «Подтвердить»
 *  - onDelete:      (order)=>void — если передан, будет красная кнопка удаления (удаляет весь чек)
 *  - onChangeAdmin: (order)=>void — если передан, в раскрытии будет «Админ … / Изменить».
 *                   Сам карандаш показывается только при order.can_set_administrator.
 *  - busy:          boolean — блокирует кнопки
 */
export default function CheckCard({ group, onConfirm, onDelete, onChangeAdmin, onCancel, busy = false }) {
    const [expanded, setExpanded] = useState(false);
    if (!group || group.length === 0) return null;

    const head         = group[0];
    const groupTotal   = group.reduce((s, o) => s + parseFloat(o.value || 0), 0);
    const allItems     = group.flatMap(o => o.items || []);
    const allDiscounts = group.flatMap(o => o.discounts_applied || []);
    const groupComment = group.map(o => o.comment).find(c => c && c.trim()) || '';
    const isPending    = group.some(o => o.confirmation_status === 'pending');
    const isCancelled  = group.some(o => o.is_cancelled);
    const moneyTypes   = [...new Set(group.map(o => o.money_type))];
    // Чек пришёл от фотографа (есть связанный фантом-уведомление) → показываем «Подтвердил: админ».
    // Нет фантома (sale_notification_id пустой) → чек оформил админ с нуля, строки «Подтвердил» нет.
    const isFromPhotographer = Boolean(head.sale_notification_id);

    const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(e); };
    const confirmDelete = () => {
        if (window.confirm('Удалить чек? Он уйдёт из кассы, выручка пересчитается.')) {
            group.forEach(o => onDelete(o));
        }
    };
    // Подтверждение и выбор сценария (прямая отмена / заявка) — в useOrderCancel.
    // Карточка только сообщает наверх, какой чек и на какую сумму отменяют.
    const cancelMode       = head.cancel_mode ?? 'direct';
    const hasPendingCancel = Boolean(head.pending_cancel_request_id);
    const requestCancel    = () => onCancel && onCancel(head, groupTotal);

    // Можно ли сменить админа — решает сервер (роль, точка, дата чека).
    // Старый сервер поля не отдаёт: тогда ориентируемся на наличие обработчика.
    const canSetAdmin = head.can_set_administrator ?? Boolean(onChangeAdmin);
    const confirmAll = () => group.forEach(o => o.confirmation_status === 'pending' && onConfirm && onConfirm(o));

    return (
        <Box sx={{
            bgcolor: 'var(--joy-palette-background-level1)',
            border: '1px solid var(--joy-palette-divider)',
            borderLeft: `3px solid ${isCancelled ? 'var(--joy-palette-neutral-500)' : isPending ? '#f59e0b' : '#22c55e'}`,
            borderRadius: 'var(--joy-radius-lg)',
            overflow: 'hidden',
            opacity: isCancelled ? 0.55 : 1,
        }}>
            {/* Краткая строка — кликабельна, разворачивает детали */}
            <Box
                onClick={() => setExpanded(v => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1.25,
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'background .15s',
                    '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                }}
            >
                {/* Аватар */}
                <Avatar src={head.photographer_avatar_url} size="sm" sx={{ bgcolor: 'var(--joy-palette-primary-500)', flexShrink: 0 }}>
                    {(head.photographer_name || '').charAt(0).toUpperCase()}
                </Avatar>

                {/* Имя + статус + точка·админ */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, minWidth: 0 }} noWrap>
                            {head.photographer_name}
                        </Typography>
                        <Chip size="sm" variant="soft" color={isCancelled ? 'danger' : isPending ? 'warning' : 'success'}
                            sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                            {isCancelled ? 'Отменён' : isPending ? 'Не подтверждён' : 'Подтверждён'}
                        </Chip>
                        {!isCancelled && hasPendingCancel && (
                            <Chip size="sm" variant="soft" color="warning"
                                sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                Заявка на отмену
                            </Chip>
                        )}
                        {groupComment && (
                            <ChatBubbleOutlineRounded sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', flexShrink: 0 }} />
                        )}
                    </Box>
                    {(head.location_name || (isFromPhotographer && head.processed_by_name)) && (
                        <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)', mt: 0.2, display: { xs: 'none', sm: 'block' } }}>
                            {head.location_name}
                            {isFromPhotographer && head.processed_by_name && (
                                <> · Подтвердил: <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>{head.processed_by_name}</Box>{head.processed_at && <>, {fmtDateTime(head.processed_at)}</>}</>
                            )}
                        </Typography>
                    )}
                    {/* Время чека — видно в статистике без раскрытия (в т.ч. на мобиле) */}
                    <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-primary)', fontWeight: 700, mt: 0.2 }}>
                        {head.receipt_time_display || fmtDateTime(head.receipt_datetime_effective || head.date_create)}
                    </Typography>
                    {/* Кто одобрил отмену — только для отменённых чеков */}
                    {isCancelled && head.cancelled_by_name && (
                        <Typography sx={{ fontSize: 10.5, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }} noWrap>
                            Отмену одобрил: <Box component="span" sx={{ color: 'var(--joy-palette-danger-300)', fontWeight: 700 }}>{head.cancelled_by_name}</Box>{head.cancelled_at && <>, {fmtDateTime(head.cancelled_at)}</>}
                        </Typography>
                    )}
                </Box>

                {/* Тип оплаты — колонка слева от суммы (десктоп: подписи) */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.3, alignItems: 'flex-end', flexShrink: 0 }}>
                    {moneyTypes.map(mt => {
                        const meta = MONEY_META[mt] || MONEY_META.transfer;
                        return (
                            <Chip key={mt} size="sm" sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 10, fontWeight: 700, '--Chip-minHeight': '20px' }}>
                                {meta.label}
                            </Chip>
                        );
                    })}
                </Box>

                {/* Тип оплаты — компактно буквой Н/П/Т (мобилка: видно без раскрытия) */}
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.3, alignItems: 'center', flexShrink: 0 }}>
                    {moneyTypes.map(mt => {
                        const meta = MONEY_META[mt] || MONEY_META.transfer;
                        return (
                            <Box key={mt} title={meta.label} sx={{
                                width: 22, height: 22, borderRadius: '6px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: meta.bg, color: meta.color, fontSize: 12, fontWeight: 800,
                            }}>
                                {meta.letter}
                            </Box>
                        );
                    })}
                </Box>

                {/* Сумма */}
                <Typography sx={{ fontSize: 17, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {fmtMoney(groupTotal)} ₽
                </Typography>

                {/* Стрелка раскрытия */}
                <Box sx={{
                    flexShrink: 0, display: 'flex', alignItems: 'center', color: 'var(--joy-palette-text-tertiary)',
                    transition: 'transform .2s', transform: expanded ? 'rotate(180deg)' : 'none',
                }}>
                    <KeyboardArrowDownRounded sx={{ fontSize: 20 }} />
                </Box>

                {/* Подтвердить (для неподтверждённых) */}
                {isPending && onConfirm && !isCancelled && (
                    <IconButton size="sm" variant="solid" color="success"
                        onClick={stop(confirmAll)} disabled={busy}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <CheckRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}

                {/* Отмена чека: напрямую (руководитель) или по заявке (админ / точка) */}
                {onCancel && !isCancelled && cancelMode !== 'none' && (
                    <IconButton size="sm" variant="solid"
                        color={hasPendingCancel ? 'neutral' : 'warning'}
                        onClick={stop(requestCancel)}
                        disabled={busy || hasPendingCancel}
                        title={hasPendingCancel
                            ? 'Заявка на отмену уже отправлена'
                            : cancelMode === 'request' ? 'Отменить чек по заявке' : 'Отменить чек'}
                        sx={{ flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <BlockRoundedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                )}

                {/* Удалить весь чек */}
                {onDelete && !isCancelled && (
                    <IconButton size="sm" variant="solid" color="danger"
                        onClick={stop(confirmDelete)} disabled={busy}
                        sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, flexShrink: 0, borderRadius: 'var(--joy-radius-md)' }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                )}
            </Box>

            {/* Раскрываемые детали (анимация) */}
            <AnimatePresence initial={false}>
                {expanded && (
                    <motion.div
                        key="details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box sx={{
                            px: 1.25, pt: 0.6, pb: 1.25,
                            borderTop: '1px dashed var(--joy-palette-divider)',
                            display: 'flex', flexDirection: 'column', gap: 0.9,
                        }}>
                            {/* Точка + админ — на мобиле (на ПК это видно в свёрнутой строке) */}
                            {(head.location_name || (isFromPhotographer && head.processed_by_name)) && (
                                <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                                    {head.location_name && (
                                        <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-secondary)' }}>
                                            {head.location_name}
                                        </Typography>
                                    )}
                                    {isFromPhotographer && head.processed_by_name && (
                                        <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-tertiary)' }}>
                                            · Подтвердил: <Box component="span" sx={{ color: '#06b6d4', fontWeight: 700 }}>{head.processed_by_name}</Box>{head.processed_at && <>, {fmtDateTime(head.processed_at)}</>}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {/* Позиции */}
                            <Box>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
                                    Позиции
                                </Typography>
                                {allItems.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        {allItems.map((it, i) => (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                    {it.product_name_snapshot}{it.quantity > 1 ? ` × ${it.quantity}` : ''}
                                                </Typography>
                                                {it.unit_price != null && (
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                        {fmtMoney(it.unit_price * it.quantity)} ₽
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Без товарных позиций (например, только электронные кадры)
                                    </Typography>
                                )}
                            </Box>

                            {/* Скидки (появляются после Этапа 1, когда сервер их сохраняет) */}
                            {allDiscounts.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4 }}>
                                        Скидки
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                        {allDiscounts.map((d, i) => (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
                                                    {d.discount_name_snapshot || 'Скидка'}
                                                    {!d.order_item && (
                                                        <Box component="span" sx={{ fontSize: 9.5, color: 'var(--joy-palette-text-tertiary)', fontWeight: 600 }}> · весь чек</Box>
                                                    )}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', whiteSpace: 'nowrap' }}>
                                                    −{d.value}{d.is_percent ? '%' : ' ₽'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Комментарий */}
                            {groupComment && (
                                <Box>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.3 }}>
                                        Комментарий
                                    </Typography>
                                    <Box sx={{ px: 1, py: 0.6, borderRadius: 'var(--joy-radius-sm)', bgcolor: 'var(--joy-palette-background-level2)', borderLeft: '3px solid #f59e0b' }}>
                                        <Typography sx={{ fontSize: 11.5, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {groupComment}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* Оплаты по чеку + времена + кто оформил */}
                            <Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
                                    {group.map(o => {
                                        const meta = MONEY_META[o.money_type] || MONEY_META.transfer;
                                        return (
                                            <Chip key={o.id} size="sm" startDecorator={meta.icon}
                                                sx={{ bgcolor: meta.bg, color: meta.color, fontSize: 9.5, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                                {meta.label} · {fmtMoney(o.value)} ₽
                                            </Chip>
                                        );
                                    })}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                    {head.created_by_name && (
                                        <>Оформил: <Box component="span" sx={{ color: '#22c55e', fontWeight: 700 }}>{head.created_by_name}</Box>, </>
                                    )}
                                    <Box component="span" sx={{ color: 'var(--joy-palette-text-primary)', fontWeight: 700 }}>{fmtDateTime(head.date_create)}</Box>
                                    {isPending && (
                                        <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700 }}> · ожидает подтверждения</Box>
                                    )}
                                </Typography>
                            </Box>

                            {/* Админ чека + изменить */}
                            {onChangeAdmin && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Админ:{' '}
                                        <Box component="span" sx={{ fontWeight: 700, color: head.administrator ? '#06b6d4' : 'var(--joy-palette-text-tertiary)' }}>
                                            {head.administrator_name || 'Без привязки'}
                                        </Box>
                                    </Typography>
                                    {canSetAdmin && (
                                        <IconButton size="sm" variant="solid" color="primary"
                                            onClick={stop(() => onChangeAdmin(head))} disabled={busy}
                                            title="Сменить админа чека"
                                            sx={{ borderRadius: 'var(--joy-radius-md)' }}>
                                            <EditRoundedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}